/**
 * Tenant warehouse Attendance list (`GET /admin/attendance`).
 *
 * Default query: last 30 local calendar days. No `deleted` param.
 * Member name links to `/users/{sourceClientId}` only when `memberMatch === 'MATCHED'`
 * (WSRADM-0044). Product name links to `/products/{sourceProductId}` only when
 * `productMatch === 'MATCHED'` (WSRADM-0045).
 */
import { useMemo, useState } from 'react';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { lastThirtyLocalDays } from '@/api/warehouse/dateWindow';
import { listAttendance } from '@/api/warehouse/warehouseApi';
import type { NameMatch, WarehouseAttendance } from '@/api/warehouse/types';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import { EM_DASH, textOrDash } from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

function matchTitle(
  match: NameMatch,
  t: (key: string, fallback?: string) => string,
): string | undefined {
  if (match === 'AMBIGUOUS') {
    return t(
      'warehouse.attendance.matchAmbiguous',
      "Several members share this name, so this row can't be linked to one.",
    );
  }
  if (match === 'UNMATCHED') {
    return t('warehouse.attendance.matchUnmatched', 'No member matches this name.');
  }
  return undefined;
}

function MemberNameCell({
  row,
  t,
}: {
  row: WarehouseAttendance;
  t: (key: string, fallback?: string) => string;
}) {
  const label = textOrDash(row.memberName);
  if (row.memberMatch === 'MATCHED' && row.sourceClientId !== null) {
    return (
      <Link component={RouterLink} to={`/users/${row.sourceClientId}`}>
        {label}
      </Link>
    );
  }
  const title = matchTitle(row.memberMatch, t);
  if (title !== undefined) {
    return <span title={title}>{label}</span>;
  }
  return label;
}

function ProductNameCell({
  row,
  t,
}: {
  row: WarehouseAttendance;
  t: (key: string, fallback?: string) => string;
}) {
  if (row.productMatch === 'NONE') {
    return EM_DASH;
  }
  const label = textOrDash(row.productName);
  if (row.productMatch === 'MATCHED' && row.sourceProductId !== null) {
    return (
      <Link component={RouterLink} to={`/products/${row.sourceProductId}`}>
        {label}
      </Link>
    );
  }
  if (row.productMatch === 'AMBIGUOUS' || row.productMatch === 'UNMATCHED') {
    const title = matchTitle(row.productMatch, t);
    return title !== undefined ? <span title={title}>{label}</span> : label;
  }
  return label;
}

export function AttendancePage() {
  const { t } = useCopy();
  const title = t('page.attendance.title', 'Attendance');
  useDocumentTitle(title);
  const window = lastThirtyLocalDays();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(window.from);
  const [to, setTo] = useState(window.to);
  const [sort, setSort] = useState('attendedOn');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const resource = useResource(
    () => listAttendance({ page, size, search, from, to, sort, order }),
    [page, size, search, from, to, sort, order],
  );

  const columns = useMemo<RosterColumn<WarehouseAttendance>[]>(
    () => [
      {
        id: 'attendedOn',
        header: t('attendance.columns.attendedOn', 'Date'),
        sortId: 'attendedOn',
        defaultOrder: 'desc',
        render: (row) => row.attendedOn,
      },
      {
        id: 'attendedAtTime',
        header: t('attendance.columns.attendedAtTime', 'Time'),
        render: (row) => textOrDash(row.attendedAtTime),
      },
      {
        id: 'memberName',
        header: t('attendance.columns.memberName', 'Member'),
        sortId: 'memberName',
        render: (row) => <MemberNameCell row={row} t={t} />,
      },
      {
        id: 'className',
        header: t('attendance.columns.className', 'Class'),
        sortId: 'className',
        render: (row) => textOrDash(row.className),
      },
      {
        id: 'instructor',
        header: t('attendance.columns.instructor', 'Instructor'),
        sortId: 'instructor',
        render: (row) => textOrDash(row.instructor),
      },
      {
        id: 'productName',
        header: t('attendance.columns.productName', 'Product'),
        sortId: 'productName',
        render: (row) => <ProductNameCell row={row} t={t} />,
      },
    ],
    [t],
  );

  return (
    <RosterTable
      title={title}
      columns={columns}
      rows={resource.data?.content ?? []}
      rowId={(row) => row.naturalKeyHash}
      status={resource.status}
      error={resource.error}
      totalElements={resource.data?.totalElements ?? 0}
      page={page}
      size={size}
      showDates
      initialFrom={window.from}
      initialTo={window.to}
      sort={sort}
      order={order}
      onPageChange={setPage}
      onRowsPerPageChange={(next) => {
        setSize(next);
        setPage(0);
      }}
      onSortChange={(id, nextOrder) => {
        setSort(id);
        setOrder(nextOrder);
        setPage(0);
      }}
      onApply={(query) => {
        setSearch(query.search);
        setFrom(query.from);
        setTo(query.to);
        setPage(0);
      }}
      onRetry={resource.refetch}
    />
  );
}
